import { memo, Suspense, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import clsx from 'clsx';

import type { AvatarSceneProps } from './AvatarScene.types';
import { Avatar } from './Avatar/Avatar';
import classes from './AvatarScene.module.css';
import { Card } from '../Card';
import CubeAnimation from './Avatar/CubeAnimation';
import { useProgress } from '@react-three/drei';
import { Loader } from '../Loader';

export const AvatarScene = memo((props: AvatarSceneProps) => {
    const { mode, className, cubeParams, animationLoading, timer } = props;
    const { progress } = useProgress();

    const showAvatarAnimation = process.env.REACT_APP_SHOW_AVATAR_ANIMATION ?? 'false';

    useEffect(() => {
        if (animationLoading) {
            animationLoading(showAvatarAnimation === 'false' ? false : progress !== 100);
        }
    }, [progress, animationLoading]);

    /**
     * Environment variable to determine whether the AVATAR ANIMATION should be displayed or not in the application.
     * Which is managed by the the pipeline environment variable
     */

    if (showAvatarAnimation === 'false')
        return (
            <div className={clsx({ [classes.talking]: mode === 'TALKING' })}>
                <CubeAnimation mode={mode.toLowerCase()} cubeParams={cubeParams} />
            </div>
        );
    else
        return (
            <Card className={clsx(classes.root, className, { [classes.talking]: mode === 'TALKING' })}>
                <Suspense
                    fallback={
                        <div className={classes['fallback-container']}>
                            <div>
                                <Loader id="avatar" micAccess={false} />
                            </div>
                        </div>
                    }
                >
                    {progress === 100 && !!timer && timer <= 5 && (
                        <div className={classes.timer}>
                            <span>
                                {timer}
                            </span>
                        </div>
                    )}
                    <div style={{ opacity: progress !== 100 ? 0 : 1, height: '100%' }}>
                        <Canvas
                            camera={{
                                fov: 25,
                            }}
                            className={classes.canvas}
                        >
                            <Avatar mode={mode} />
                        </Canvas>
                    </div>
                </Suspense>
            </Card>
        );
});

AvatarScene.displayName = 'AvatarScene';
